import { Select, SelectProps, useMantineTheme } from "@mantine/core";
import { ArrowDown2 } from "iconsax-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMyPlans, useMyProducts } from "../../../hooks";
import { useAuth } from "../../../context/auth";
import { ERole } from "../../../interfaces/User/newUser";
import CustomSelectItem from "./CustomSelectItem";
import { EServiceType } from "../../../interfaces/MyPlans/IMyPlans";

interface ProductsSelect {
  value: string;
  label: string;
  isChecked: boolean;
}

interface IProduct {
  id: number;
  title: string;
}

// Tipo genérico para aceitar diferentes tipos de query
type QueryType = {
  productId?: number[];
  productsId?: number[];
  [key: string]: any;
};

interface IProps<T extends QueryType>
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  query: T;
  setQuery: React.Dispatch<React.SetStateAction<T>>;
  // Propriedade que contém os IDs dos produtos (pode ser 'productId' ou 'productsId')
  productIdKey?: "productId" | "productsId";
}

const MultiSelectProducts = <T extends QueryType>({
  query,
  setQuery,
  productIdKey = "productId",
  ...props
}: IProps<T>) => {
  const theme = useMantineTheme();
  const { roleSelected } = useAuth();
  const { t } = useTranslation();
  const roleSelectedIsAnalyst = roleSelected === ERole.analyst;
  const roleSelectedIsClient = roleSelected === ERole.client;

  const { data: dataMyProducts, isFetching: isLoadingMyProducts } =
    useMyProducts({
      enabled: roleSelectedIsAnalyst,
      active: EServiceType.All,
    });

  const { data: dataMyPlans, isFetching: isLoadingMyPlans } = useMyPlans({
    status: "",
    enabled: roleSelectedIsClient,
  });

  const [products, setProducts] = useState<ProductsSelect[]>(
    [] as ProductsSelect[]
  );

  const hasData =
    (!!dataMyPlans && dataMyPlans.dates.length > 0) || !!dataMyProducts;
  const loading = isLoadingMyProducts || isLoadingMyPlans;

  // Helper para obter os IDs selecionados
  const getSelectedIds = (): number[] => {
    return (query[productIdKey] as number[] | undefined) || [];
  };

  // Helper para atualizar os IDs selecionados
  const updateSelectedIds = (ids: number[] | undefined) => {
    setQuery((prev) => ({
      ...prev,
      [productIdKey]: ids,
    }));
  };

  useEffect(() => {
    if (hasData) {
      const rawProducts: IProduct[] = roleSelectedIsAnalyst
        ? dataMyProducts?.data ?? []
        : dataMyPlans?.dates?.flatMap((date) =>
            date.user_plans.map((plan) => plan.product)
          ) ?? [];

      const productsData: IProduct[] = Array.from(
        new Map(rawProducts.map((product) => [product.id, product])).values()
      );

      // Compara se os produtos disponíveis mudaram
      const currentProductIds = productsData
        .map((p) => p.id)
        .sort()
        .join(",");
      const previousProductIds = products
        .filter((p) => p.value !== "")
        .map((p) => Number(p.value))
        .sort()
        .join(",");

      // Se os produtos disponíveis não mudaram e já temos produtos carregados, não atualiza
      if (products.length > 0 && currentProductIds === previousProductIds) {
        return;
      }

      let arrayProducts: ProductsSelect[] = [];

      if (productsData.length === 1) {
        // Para um único produto, sempre seleciona automaticamente
        const singleProductId = productsData[0].id;
        const currentIds = getSelectedIds();
        // Só atualiza a query se o produto selecionado for diferente
        if (currentIds.length === 0 || currentIds[0] !== singleProductId) {
          updateSelectedIds([singleProductId]);
        }
        arrayProducts = [
          {
            value: singleProductId.toString(),
            label: productsData[0].title,
            isChecked: true,
          },
        ];
      } else {
        // Para múltiplos produtos, preserva a seleção atual ou define estado inicial
        const currentSelectedIds = getSelectedIds();
        const hasCurrentSelection = currentSelectedIds.length > 0;

        // Verifica se todos os serviços atuais estão selecionados
        const allCurrentProductsSelected =
          currentSelectedIds.length > 0 &&
          productsData.every((product) =>
            currentSelectedIds.includes(product.id)
          );

        // Verifica se nenhum serviço está selecionado
        const noProductsSelected =
          currentSelectedIds.length === 0 ||
          !productsData.some((product) =>
            currentSelectedIds.includes(product.id)
          );

        arrayProducts = [
          {
            value: "",
            label: t("operacoes.mock.all_products"),
            isChecked: allCurrentProductsSelected || noProductsSelected,
          },
          ...productsData.map((product) => ({
            value: product.id.toString(),
            label: product.title,
            isChecked: hasCurrentSelection
              ? currentSelectedIds.includes(product.id)
              : false,
          })),
        ];
      }

      setProducts(arrayProducts);
    } else {
      setProducts([
        { value: "", label: t("operacoes.mock.no_products"), isChecked: true },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataMyPlans, dataMyProducts, roleSelectedIsAnalyst]);

  // Sincroniza o estado dos produtos quando a query muda externamente
  useEffect(() => {
    if (products.length > 1) {
      // Só sincroniza se há múltiplos produtos
      const currentSelectedIds = getSelectedIds();

      // Compara arrays para evitar atualizações desnecessárias
      const currentIdsString = JSON.stringify([...currentSelectedIds].sort());
      const productsIdsString = JSON.stringify(
        products
          .filter((p) => p.isChecked && p.value !== "")
          .map((p) => Number(p.value))
          .sort()
      );

      // Se já estão sincronizados, não atualiza
      if (currentIdsString === productsIdsString) {
        return;
      }

      const updatedProducts = products.map((product) => {
        if (product.value === "") {
          // Para o item "todos", verifica se todos os serviços estão selecionados
          const allSelected =
            currentSelectedIds.length > 0 &&
            products
              .filter((p) => p.value !== "")
              .every((p) => currentSelectedIds.includes(Number(p.value)));
          return { ...product, isChecked: allSelected };
        } else {
          // Para serviços individuais, verifica se estão na seleção atual
          return {
            ...product,
            isChecked: currentSelectedIds.includes(Number(product.value)),
          };
        }
      });

      // Só atualiza se houve mudança real
      const hasChanges = updatedProducts.some(
        (product, index) => product.isChecked !== products[index].isChecked
      );

      if (hasChanges) {
        setProducts(updatedProducts);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query[productIdKey]]);

  const updateProductSelection = (
    products: ProductsSelect[],
    value: string
  ) => {
    let updated = [...products];

    if (value === "") {
      return updated.map((p) => ({ ...p, isChecked: p.value === "" }));
    }

    updated = updated.map((p) => ({
      ...p,
      isChecked:
        p.value === "" ? false : p.value === value ? !p.isChecked : p.isChecked,
    }));
    const individual = updated.filter((p) => p.value !== "");
    const allSelected =
      individual.length > 0 && individual.every((p) => p.isChecked);
    const noneSelected =
      individual.length > 0 && individual.every((p) => !p.isChecked);

    return allSelected || noneSelected
      ? updated.map((p) => ({ ...p, isChecked: p.value === "" }))
      : updated;
  };

  const handleSelectChange = (value: string | null) => {
    if (products.length === 1) return;
    if (value === null) return;

    const updatedProducts = updateProductSelection(products, value);
    const selectedIds = updatedProducts
      .filter((p) => p.isChecked && p.value !== "")
      .map((p) => Number(p.value));

    setProducts(updatedProducts);
    updateSelectedIds(selectedIds.length > 0 ? selectedIds : undefined);
  };

  const selectedProducts = products.filter(
    (p) => p.isChecked && p.value !== ""
  );

  const selectDisplayValue = useMemo(() => {
    if (products.length === 1) return products[0].label;
    if (selectedProducts.length >= 1) {
      return t("operacoes.multi_select_products.selected_products", {
        qtd: selectedProducts.length,
      });
    }
    return t("operacoes.mock.all_products");
  }, [selectedProducts, products, t]);

  return (
    <Select
      mt={-8}
      styles={{
        rightSection: { pointerEvents: "none" },
        input: { color: theme.colors.basic[5] },
      }}
      aria-label="select"
      dropdownPosition="bottom"
      rightSection={<ArrowDown2 size="24" color={theme.colors.basic[5]} />}
      label={t("operacoes.multi_select_products.select_products")}
      placeholder={
        loading
          ? t("operacoes.multi_select_products.loading_products")
          : selectDisplayValue
      }
      w="100%"
      searchable
      disabled={!hasData || loading}
      data={products}
      value={selectDisplayValue}
      onChange={handleSelectChange}
      itemComponent={CustomSelectItem}
      {...props}
    />
  );
};

export default MultiSelectProducts;
